import { gql, useQuery } from '@apollo/client'
import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import Select from 'react-select'
import styled from 'styled-components'

import {
  Breadcrumb,
  ErrorText,
  FixedWidthPageContainer,
  Table,
} from '../common/common-components'
import {
  MeasurerDeployDashboardQuery,
  MeasurerDeployDashboardQueryVariables,
} from '../common/generated'
import { transformMeasurerVersion } from '../common/measurer-version'
import { MeasurerVersionDisplay } from '../common/measurer-version-display'
import { StatusBadge } from '../common/status-badge'

const MEASURER_DEPLOY_DASHBOARD_QUERY = gql`
  query MeasurerDeployDashboard {
    allMeasurers {
      nodes {
        id
        name
        functionName
        repoSlug
        measurerVersionsByMeasurerId(orderBy: ID_DESC) {
          nodes {
            id
            tagName
            sha1
            commitDate
            commitMessage
            stateMachineStateId
            error
            promiseStateMachineStatesEnumByStateMachineStateId {
              name
            }
          }
        }
      }
    }
  }
`

const ErrorBlock = styled.pre`
  background: #fff3f3;
  border: 1px solid #f5c6cb;
  border-radius: 3px;
  padding: 8px;
  font-size: 11px;
  white-space: pre-wrap;
  word-break: break-word;
  max-width: 600px;
  margin: 4px 0 0 0;
`

const VersionTable = styled(Table)`
  width: 100%;
  border-collapse: collapse;

  th {
    padding-right: 2em;
    width: auto;
  }

  td {
    padding: 6px 2em 6px 0;
    border-bottom: 1px solid #eee;
    vertical-align: top;
  }

  tr:last-child td {
    border-bottom: none;
  }
`

const MeasurerSelectContainer = styled.div`
  width: 300px;
  margin: 16px 0 24px 0;
`

function MeasurerVersionsTable({
  measurer,
}: {
  measurer: MeasurerDeployDashboardQuery['allMeasurers']['nodes'][number]
}): JSX.Element {
  const versions = measurer.measurerVersionsByMeasurerId.nodes
  if (versions.length === 0) {
    return <p>No versions found for this measurer.</p>
  }

  return (
    <VersionTable>
      <thead>
        <tr>
          <th>ID</th>
          <th>Version</th>
          <th>Commit</th>
          <th>Status</th>
          <th>Error</th>
        </tr>
      </thead>
      <tbody>
        {versions.map(v => {
          const stateName =
            v.promiseStateMachineStatesEnumByStateMachineStateId.name
          const isErrored =
            stateName.toLowerCase().includes('error') ||
            (v.error !== null && v.error.length > 0)

          return (
            <tr key={v.id}>
              <td>
                <code>{v.id}</code>
              </td>
              <td>
                <MeasurerVersionDisplay
                  measurerVersion={transformMeasurerVersion({
                    measurerName: measurer.name,
                    functionName: measurer.functionName,
                    repoSlug: measurer.repoSlug,
                    sha1: v.sha1,
                    tagName: v.tagName,
                    commitDate: v.commitDate,
                    commitMessage: v.commitMessage,
                  })}
                />
              </td>
              <td>
                {v.commitDate ? new Date(v.commitDate).toLocaleString() : ''}
                <br />
                {v.commitMessage}
              </td>
              <td>
                <StatusBadge state={stateName}>{stateName}</StatusBadge>
              </td>
              <td>
                {isErrored && v.error && v.error.length > 0 ? (
                  <ErrorBlock>{v.error.join('\n')}</ErrorBlock>
                ) : (
                  '—'
                )}
              </td>
            </tr>
          )
        })}
      </tbody>
    </VersionTable>
  )
}

export function MeasurerDeployment(): JSX.Element {
  const { loading, error, data } = useQuery<
    MeasurerDeployDashboardQuery,
    MeasurerDeployDashboardQueryVariables
  >(MEASURER_DEPLOY_DASHBOARD_QUERY)

  const [selectedMeasurerId, setSelectedMeasurerId] = useState<number | null>(
    null
  )

  const measurers = data?.allMeasurers.nodes ?? []

  const options = measurers.map(m => ({ value: m.id, label: m.name }))

  const selectedMeasurer =
    selectedMeasurerId !== null
      ? measurers.find(m => m.id === selectedMeasurerId) ?? null
      : null

  return (
    <FixedWidthPageContainer>
      <Breadcrumb>
        <Link to="/">Home</Link> {'>'} Measurer deploy dashboard
      </Breadcrumb>
      <h1>Measurer deploy dashboard</h1>

      {error && <ErrorText>Oh no! {error.message}</ErrorText>}
      {loading && <p>Loading...</p>}

      {!loading && (
        <>
          <MeasurerSelectContainer>
            <Select
              options={options}
              placeholder="Select a measurer"
              onChange={opt => setSelectedMeasurerId(opt ? opt.value : null)}
              isClearable
            />
          </MeasurerSelectContainer>

          {selectedMeasurer && (
            <>
              <h2>{selectedMeasurer.name}</h2>
              <MeasurerVersionsTable measurer={selectedMeasurer} />
            </>
          )}

          {selectedMeasurerId === null && !loading && (
            <p>Select a measurer above to view deploy versions.</p>
          )}
        </>
      )}
    </FixedWidthPageContainer>
  )
}
